import { useState, useContext, useEffect } from "react";

// @mui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import CreatableSelect from "components/UpStyled/CreatableSelect";
import SuiBadge from "components/SuiBadge";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddBoxIcon from "@mui/icons-material/AddBox";

import toast from "react-hot-toast";
import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { capitalCase } from "capital-case";

import { StoreContext } from "context/store";
import { maCatOptions } from "utils/data";

const TAG_MAX_LENGTH = 25;
const TAG_MAX_QUANTITY = 24;

const ProjectRequirement = ({ onNext, onPrev, isNextDisabled, isPrevDisabled, saveNext }) => {
  const { store, updateStore } = useContext(StoreContext);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [newCat, setNewCat] = useState("");

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors },
    control,
  } = useForm();

  useEffect(() => {
    if (store?.metadata?.manufacturing_specialties) {
      setSelectedCategories(store.metadata.manufacturing_specialties);
    }
    //set intial values
    reset({
      moq: store?.metadata?.moq,
      costs_price_range_min: store?.metadata?.costs_price_range_min,
      costs_price_range_max: store?.metadata?.costs_price_range_max,
      avg_sample_lead_time: store?.metadata?.avg_sample_lead_time,
      avg_production_lead_time: store?.metadata?.avg_production_lead_time,
    });
  }, [store]);

  const handleSave = async (data) => {
    if (data["costs_price_range_max"]) {
      data["costs_price_range_min"] = data["costs_price_range_min"] || 0;
    }
    const cost_min = data["costs_price_range_min"] ? Number(data["costs_price_range_min"]) : 0;
    const cost_max = data["costs_price_range_max"] ? Number(data["costs_price_range_max"]) : 0;
    if (cost_min > cost_max) {
      return toast.error("Costs price range is invalid!");
    }

    const payload = {
      metadata: {
        moq: data["moq"],
        costs_price_range_min: data["costs_price_range_min"],
        costs_price_range_max: data["costs_price_range_max"],
        avg_production_lead_time: data["avg_production_lead_time"],
        avg_sample_lead_time: data["avg_sample_lead_time"],
        manufacturing_specialties: selectedCategories,
      },
    };
    try {
      await updateStore(payload);
      toast.success("Update manufacturing details successfully!");
      saveNext();
    } catch (err) {
      return toast.error("Update manufacturing details failed!");
    }
  };

  const handleNewCatChange = (keyCode = "") => {
    const value = newCat;

    if (keyCode == "Comma" || keyCode == "Enter") {
      const newValue = value.trim().replace(",", "").toUpperCase();
      if (selectedCategories.length >= TAG_MAX_QUANTITY) {
        return toast.error(`You can have at most ${TAG_MAX_QUANTITY} categories!`);
      } else if (newValue.length >= TAG_MAX_LENGTH) {
        return toast.error(
          `Your customized tag can not have more than ${TAG_MAX_LENGTH} characters!`
        );
      } else if (newValue.length == 0) {
        return toast.error("Please input your customized category!");
      } else if (selectedCategories.includes(newValue)) {
        return toast.error("You already have the category!");
      }

      setSelectedCategories([...selectedCategories, newValue]);
      setNewCat("");
    }
  };

  const handleAddCat = (e) => {
    const newValue = newCat.trim().replace(",", "").toUpperCase();

    if (selectedCategories.length >= TAG_MAX_QUANTITY) {
      return toast.error(`You can have at most ${TAG_MAX_QUANTITY} categories!`);
    } else if (!newValue) {
      return toast.error("Please input your customized category!");
    } else if (newValue.length >= TAG_MAX_LENGTH) {
      return toast.error(
        `Your customized tag can not have more than ${TAG_MAX_LENGTH} characters!`
      );
    } else if (selectedCategories.includes(newValue)) {
      return toast.error("You already have the category!");
    }
    setSelectedCategories([...selectedCategories, newValue]);
    setNewCat("");
  };

  const handleSelectCat = (value) => {
    if (selectedCategories.includes(value)) {
      setSelectedCategories(selectedCategories.filter((x) => x != value));
    } else {
      if (selectedCategories.length >= TAG_MAX_QUANTITY) {
        return toast.error(`You can have at most ${TAG_MAX_QUANTITY} categories!`);
      }
      setSelectedCategories([...selectedCategories, value]);
    }
  };

  const handleRemoveTags = (value) => {
    setSelectedCategories(selectedCategories.filter((x) => x != value));
  };

  return (
    <SuiBox component="form" onSubmit={handleSubmit(handleSave)}>
      <Card id="project-requirement" sx={{ overflow: "visible" }} className="my-2">
        <SuiBox p={3}>
          <SuiTypography variant="h5">Project Specifications</SuiTypography>
        </SuiBox>
        <SuiBox pb={3} px={3}>
          <Grid container spacing={3} className="items-start">
            <Grid item xs={12} sm={6}>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Minimum Order Quantity(MOQ)
                  </SuiTypography>
                </SuiBox>
                <SuiInput
                  placeholder="MOQ"
                  type="number"
                  componentsProps={{ input: { min: 1 } }}
                  {...register("moq", {
                    min: { value: 1, message: "Value should be greater than 0!" },
                    valueAsNumber: true,
                  })}
                />
                <ErrorMessage
                  errors={errors}
                  name="moq"
                  render={({ message }) => (
                    <SuiTypography
                      variant="text"
                      fontWeight="regular"
                      fontSize="12px"
                      color="error"
                      className="ml-2"
                    >
                      {message}
                    </SuiTypography>
                  )}
                />
              </SuiBox>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Manufacturing Costs Price Range
                  </SuiTypography>
                </SuiBox>
                <div className="flex">
                  <SuiInput
                    icon={{ direction: "left", component: <AttachMoneyIcon /> }}
                    placeholder="Min"
                    type="number"
                    componentsProps={{ input: { min: 0 } }}
                    {...register("costs_price_range_min", {
                      min: {
                        value: 0,
                        message: "Mininal costs should be greater or equal than 0!",
                      },
                    })}
                  />
                  <Divider style={{ width: 20 }} />
                  <SuiInput
                    icon={{ direction: "left", component: <AttachMoneyIcon /> }}
                    placeholder="Max"
                    type="number"
                    componentsProps={{ input: { min: 1 } }}
                    {...register("costs_price_range_max", {
                      min: { value: 1, message: "Maxmium costs should be greater than 0!" },
                    })}
                  />
                </div>
                {errors.costs_price_range_min?.type == "min" ? (
                  <ErrorMessage
                    errors={errors}
                    name="costs_price_range_min"
                    render={({ message }) => (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        className="ml-2"
                      >
                        {message}
                      </SuiTypography>
                    )}
                  />
                ) : (
                  <ErrorMessage
                    errors={errors}
                    name="costs_price_range_max"
                    render={({ message }) => (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        className="ml-2"
                      >
                        {message}
                      </SuiTypography>
                    )}
                  />
                )}
              </SuiBox>
            </Grid>

            <Grid item xs={12} sm={6}>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Average Sample Production Lead Time
                  </SuiTypography>
                </SuiBox>
                <div className="flex items-center">
                  <SuiInput
                    placeholder="Sample Lead Time"
                    type="number"
                    componentsProps={{ input: { min: 1 } }}
                    {...register("avg_sample_lead_time", {
                      min: {
                        value: 1,
                        message: "Average sample lead time should be greater than 0!",
                      },
                    })}
                  />
                  <div className="flex whitespace-nowrap ml-1	">Day(s)</div>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="avg_sample_lead_time"
                  render={({ message }) => (
                    <SuiTypography
                      variant="text"
                      fontWeight="regular"
                      fontSize="12px"
                      color="error"
                      className="ml-2"
                    >
                      {message}
                    </SuiTypography>
                  )}
                />
              </SuiBox>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Average Production Lead Time
                  </SuiTypography>
                </SuiBox>
                <div className="flex items-center">
                  <SuiInput
                    placeholder="Production Lead Time"
                    type="number"
                    componentsProps={{ input: { min: 1 } }}
                    {...register("avg_production_lead_time", {
                      min: {
                        value: 1,
                        message: "Average production lead time should be greater than 0!",
                      },
                    })}
                  />
                  <div className="flex whitespace-nowrap ml-1	">Day(s)</div>
                </div>
                <ErrorMessage
                  errors={errors}
                  name="avg_production_lead_time"
                  render={({ message }) => (
                    <SuiTypography
                      variant="text"
                      fontWeight="regular"
                      fontSize="12px"
                      color="error"
                      className="ml-2"
                    >
                      {message}
                    </SuiTypography>
                  )}
                />
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>
      </Card>
      <Card id="manufaturing-specialties" sx={{ overflow: "visible" }} className="my-2">
        <SuiBox p={3}>
          <SuiTypography variant="h5">Manufacturing Specialties</SuiTypography>
          <div>
            {selectedCategories && selectedCategories.length == TAG_MAX_QUANTITY ? (
              <SuiTypography variant="text" fontWeight="regular" fontSize="12px" color="error">
                You can have at most {TAG_MAX_QUANTITY} categories!
              </SuiTypography>
            ) : (
              ""
            )}
          </div>
        </SuiBox>
        <SuiBox pb={3} px={3}>
          <Grid container spacing={3} className="items-start">
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Your Specialties
                  </SuiTypography>
                </SuiBox>

                <div className={`flex flex-col flex-wrap max-h-[360px]`}>
                  {selectedCategories.map((cat, index) => (
                    <SuiBadge
                      key={index}
                      variant="contained"
                      color="info"
                      textTransform="none"
                      className="hover:cursor-pointer mr-1 mb-1"
                      badgeContent={
                        <div className="flex items-center">
                          {capitalCase(cat, { stripRegexp: /[^A-Z0-9\'\&\/-_]/gi })}
                          <div
                            className="hover:cursor-pointer"
                            onClick={(e) => handleRemoveTags(cat)}
                          >
                            <div className="text-gray-400 ml-2">X</div>
                          </div>
                        </div>
                      }
                      container
                    />
                  ))}
                </div>
              </SuiBox>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={6}>
              <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
                <SuiBox mb={1} ml={1} lineHeight={0} display="inline-block">
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Product List
                  </SuiTypography>
                </SuiBox>
                <div className="medium:border-solid medium:border-l-1 medium:border-y-0 medium:border-r-0">
                  <div className="flex flex-col flex-wrap h-[360px] pl-2">
                    {maCatOptions.map((cat, index) => (
                      <div className="mb-1">
                        <Checkbox
                          key={index}
                          checked={selectedCategories.includes(cat.value) ? true : false}
                          defaultChecked={selectedCategories.includes(cat.value) ? true : false}
                          onClick={() => handleSelectCat(cat.value)}
                        />
                        <SuiBox lineHeight={0} display="inline-block">
                          <SuiTypography
                            component="label"
                            variant="body"
                            fontWeight="regular"
                            textTransform="capitalize"
                            color={"dark"}
                            className="cursor-pointer"
                            onClick={() => handleSelectCat(cat.value)}
                          >
                            {capitalCase(cat.label, {
                              delimiter: " ",
                              stripRegexp: /[^A-Z0-9\'\&\/-_]/gi,
                            })}
                          </SuiTypography>
                        </SuiBox>
                      </div>
                    ))}
                  </div>
                </div>
                <SuiInput
                  onChange={(e) => {
                    if (e?.target?.value) {
                      setNewCat(e.target.value.toUpperCase());
                    } else {
                      setNewCat("");
                    }
                  }}
                  value={newCat}
                  onKeyDown={(e) => {
                    if (e.code == "Enter" || e.code == "Comma") {
                      e.preventDefault();
                      handleNewCatChange(e.code);
                    }

                    return e;
                  }}
                  componentsProps={{ input: { maxlength: TAG_MAX_LENGTH } }}
                  placeholder="Use ',' to confirm your customized category."
                  icon={{
                    direction: "right",
                    component: (
                      <AddBoxIcon className="hover:cursor-pointer" onClick={handleAddCat} />
                    ),
                  }}
                  maxLength={TAG_MAX_LENGTH}
                />
                <SuiBox display="flex" justifyContent="space-between">
                  <div>
                    {newCat.length > TAG_MAX_LENGTH ? (
                      <SuiTypography
                        variant="text"
                        fontWeight="regular"
                        fontSize="12px"
                        color="error"
                        sx={{ ml: 2 }}
                      >
                        Maxmium length should not exceed {TAG_MAX_LENGTH} characters
                      </SuiTypography>
                    ) : (
                      ""
                    )}
                  </div>
                  <SuiTypography
                    variant="text"
                    color="secondary"
                    fontWeight="regular"
                    fontSize="14px"
                  >
                    {newCat ? newCat.length : 0}/{TAG_MAX_LENGTH}
                  </SuiTypography>
                </SuiBox>
              </SuiBox>
            </Grid>
          </Grid>
          <SuiBox
            pt={3}
            px={3}
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            {onPrev ? (
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton
                    variant="gradient"
                    color={isPrevDisabled ? "light" : "dark"}
                    sx={{ height: "100%" }}
                    onClick={onPrev}
                    disabled={isPrevDisabled}
                  >
                    <ArrowBackIcon fontSize="large" />
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            ) : (
              <div />
            )}
            <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                <SuiButton variant="gradient" color="dark" sx={{ height: "100%" }} type="submit">
                  save
                </SuiButton>
              </SuiBox>
            </SuiBox>
            {onNext ? (
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton
                    variant="gradient"
                    color={isNextDisabled ? "light" : "dark"}
                    sx={{ height: "100%" }}
                    onClick={onNext}
                    disabled={isNextDisabled}
                  >
                    <ArrowForwardIcon fontSize="large" />
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            ) : null}
          </SuiBox>
        </SuiBox>
      </Card>
    </SuiBox>
  );
};

export default ProjectRequirement;
